<template>
  <CustomModal ref="modal" :title="isEdit ? 'Modifier le fournisseur' : 'Nouveau fournisseur'" class="supplier-modal">
    <div class="container">
      <CustomForm ref="form" :inputs=inputs :submit=buttons.submit @submit="preventNativeSubmit">
        <CustomTextField :content=inputs.name />
        <div class="fields-row">
          <CustomTextField :content=inputs.siret />
          <CustomTextField :content=inputs.vatNumber />
        </div>
        <div class="fields-row">
          <CustomTextField :content=inputs.email />
          <CustomTextField :content=inputs.phone />
        </div>
        <CustomTextField :content=inputs.street />
        <div class="fields-row three">
          <CustomTextField :content=inputs.postalCode />
          <CustomTextField :content=inputs.city />
          <CustomTextField :content=inputs.country />
        </div>
        <div class="fields-row">
          <CustomTextField :content=inputs.paymentTermDays />
          <CustomSelectField :content=inputs.defaultCategory />
        </div>
        <CustomTextField :content=inputs.notes />
        <div class="button-container">
          <CustomButton class="button" @click="submitForm" :params=buttons.submit />
          <CustomButton class="button" :is-secondary="true" @click="closeModal()" :params=buttons.cancel />
        </div>
      </CustomForm>
    </div>
  </CustomModal>
</template>

<script>
import CustomButton from "@/components/forms/customs/customButton/CustomButton.vue";
import CustomTextField from "@/components/forms/customs/customTextField/CustomTextField.vue";
import CustomSelectField from "@/components/forms/customs/customSelectField/CustomSelectField.vue";
import CustomForm from "@/components/forms/CustomForm.vue";
import CustomModal from "@/components/modals/CustomModal.vue";
import {NotificationService} from "@vues/v-common";
import {normalizeIdentifier, SIRET_MASK, sirenFromSiret} from "@/utils";
import {SupplierController} from "@/purchase/controllers/SupplierController";
import {PURCHASE_CATEGORY_ENUM, enumToSelectValues, idOf} from "@/purchase/utils";

// Modale d'un fournisseur : création avec openModal(), modification avec openModal(fournisseur).
// callback est appelé après l'enregistrement, avec l'identifiant du fournisseur.
export default {
  name: 'SupplierModal',
  props: ['callback'],
  components: {CustomModal, CustomForm, CustomSelectField, CustomTextField, CustomButton},
  data() {
    return {
      supplierId: undefined,
      inputs: {
        name: {label: 'Nom', value: '', type: 'text', required: true},
        siret: {label: 'SIRET', value: '', type: 'text', mask: SIRET_MASK, minDigits: SIRET_MASK.maxDigits},
        vatNumber: {label: 'N° de TVA intracommunautaire', value: '', type: 'text'},
        email: {label: 'Email', value: '', type: 'text'},
        phone: {label: 'Téléphone', value: '', type: 'text'},
        street: {label: 'Adresse', value: '', type: 'text'},
        postalCode: {label: 'Code postal', value: '', type: 'text'},
        city: {label: 'Ville', value: '', type: 'text'},
        country: {label: 'Pays', value: '', type: 'text'},
        paymentTermDays: {label: 'Délai de paiement (jours)', value: '', type: 'number'},
        defaultCategory: {label: 'Catégorie d\'achat habituelle', value: '', type: 'select', selectValues: [{label: 'Aucune', value: ''}, ...enumToSelectValues(PURCHASE_CATEGORY_ENUM)]},
        notes: {label: 'Notes', value: '', type: 'text'},
      },
      buttons: {
        submit: {text: "Enregistrer"},
        cancel: {text: "Annuler"},
      }
    };
  },
  computed: {
    isEdit() {
      return this.supplierId !== undefined;
    }
  },
  methods: {
    resetInputs() {
      Object.values(this.inputs).forEach(input => {
        input.value = '';
        input.error = false;
      });
    },
    async openModal(supplier) {
      this.supplierId = undefined;
      this.resetInputs();
      this.$refs.modal.openModal();

      if (!supplier) return;

      this.supplierId = idOf(supplier);
      // Le fournisseur complet (adresse, notes...) : les lignes des tableaux n'en portent qu'une partie
      const full = await SupplierController.get(this.supplierId);
      if (!full || full.errors) {
        this.closeModal(true);
        return;
      }

      this.inputs.name.value = full.name ?? '';
      this.inputs.siret.value = normalizeIdentifier(full.siret, SIRET_MASK.maxDigits);
      this.inputs.vatNumber.value = full.vatNumber ?? '';
      this.inputs.email.value = full.email ?? '';
      this.inputs.phone.value = full.phone ?? '';
      this.inputs.street.value = full.address?.street ?? '';
      this.inputs.postalCode.value = full.address?.postalCode ?? '';
      this.inputs.city.value = full.address?.city ?? '';
      this.inputs.country.value = full.address?.country ?? '';
      this.inputs.paymentTermDays.value = full.paymentTermDays ?? '';
      this.inputs.defaultCategory.value = full.defaultCategory ?? '';
      this.inputs.notes.value = full.notes ?? '';
    },
    closeModal(event = false) {
      this.$refs.modal?.closeModal(event);
    },
    preventNativeSubmit(event) {
      event?.preventDefault?.();
    },
    text(input) {
      return String(input.value ?? '').trim();
    },
    buildBody() {
      const siret = normalizeIdentifier(this.inputs.siret.value, SIRET_MASK.maxDigits);

      // Seuls les champs d'adresse renseignés sont envoyés, une adresse vide efface l'adresse existante
      const address = {};
      ['street', 'postalCode', 'city', 'country'].forEach(key => {
        if (this.text(this.inputs[key])) address[key] = this.text(this.inputs[key]);
      });

      const paymentTermDays = this.text(this.inputs.paymentTermDays);

      return {
        name: this.text(this.inputs.name),
        siret,
        // Le SIREN correspond aux 9 premiers chiffres du SIRET
        siren: sirenFromSiret(siret),
        vatNumber: this.text(this.inputs.vatNumber),
        email: this.text(this.inputs.email),
        phone: this.text(this.inputs.phone),
        address: Object.keys(address).length > 0 ? address : null,
        paymentTermDays: paymentTermDays === '' ? null : Number(paymentTermDays),
        defaultCategory: this.inputs.defaultCategory.value || null,
        notes: this.text(this.inputs.notes),
      };
    },
    async submitForm(event) {
      this.preventNativeSubmit(event);

      if (!this.text(this.inputs.name)) {
        this.inputs.name.error = true;
        (new NotificationService).addNotification("danger", "Le nom n'est pas renseigné");
        return;
      }

      const body = this.buildBody();
      const res = await this.$refs.form.submitForm(() => this.isEdit
          ? SupplierController.patch(this.supplierId, body)
          : SupplierController.post(body));
      if (!res.errors) {
        (new NotificationService).addNotification("success", this.isEdit ? "Fournisseur modifié" : "Fournisseur créé");
        this.closeModal(true);
        if (this.callback) this.callback(this.isEdit ? this.supplierId : res.id);
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

.container
  display flex
  flex-direction column

  form
    display flex
    flex-direction column
    gap 4px

    .fields-row
      display grid
      grid-template-columns 1fr 1fr
      gap 12px

      &.three
        grid-template-columns 1fr 2fr 1fr

    // Le bouton principal est en premier dans le DOM (action par défaut de la touche Entrée),
    // l'ordre visuel est inversé : [Annuler] [Enregistrer]
    .button-container
      display flex
      flex-direction row-reverse
      justify-content flex-start
      gap 10px
      margin-top 12px

      .button
        min-width 110px

@media (max-width 480px)
  .container form .fields-row, .container form .fields-row.three
    grid-template-columns 1fr
</style>

<style lang="stylus">
.supplier-modal
  > .modal
    width 560px !important
    max-width 100%
    height auto !important
    max-height 92vh
    overflow-y auto
</style>
